import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Grid, Tooltip, Typography } from "@mui/material";
import SegmentOutlinedIcon from "@mui/icons-material/SegmentOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import _ from "lodash";
import { FormProvider, RHFFileField, RHFSearchableSelectField, RHFSelectField, RHFSwitch, RHFTextField } from "../../common/hook-form";
import RHFTextArea from "../../common/hook-form/RHFTextArea";
import { apiConstants, postGeneric } from "../../common/constants";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import SectionCard, { MUTED, gradientBtnSx, resetBtnSx } from "./SectionCard";
import TicketDateRangeField from "./TicketDateRangeField";
import { cachedGet } from "./escalationApiCache";
import { SEVERITY_OPTIONS } from "./severity";
import { DATE_RANGE_PATTERN, fieldKind } from "./ticketFieldKinds";
import { emailListProblem, emailProblem } from "./emailValidation";

// Several AWBs in one form raise one ticket each; more than this goes through Bulk Create.
const MAX_AWBS = 20;
const parseAwbs = (value) => [...new Set((value || "").split(/[\s,]+/).map((a) => a.trim()).filter(Boolean))];

/** The AWBs typed so far as chips — one ticket each — with any the last check refused in red. */
const AwbPreview = ({ value, problems }) => {
    const awbs = parseAwbs(value);
    if (awbs.length === 0) return null;
    const bad = awbs.filter((a) => problems[a]).length;
    return (
        <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12, color: bad ? "#D03B3B" : MUTED, mb: 0.5, fontWeight: 600 }}>
                {awbs.length} AWB{awbs.length === 1 ? "" : "s"} → {awbs.length} ticket{awbs.length === 1 ? "" : "s"}
                {awbs.length > MAX_AWBS ? ` · up to ${MAX_AWBS} at a time` : ""}
                {bad ? ` · ${bad} can't take a ticket` : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {awbs.map((a) => (
                    <Tooltip key={a} title={problems[a] ? `${a} ${problems[a]}` : ""} arrow>
                        <Chip size="small" label={a} variant={problems[a] ? "filled" : "outlined"}
                            sx={{ fontSize: 12, fontWeight: 600, ...(problems[a] ? { bgcolor: "#FDECEC", color: "#D03B3B" } : { color: "#663F95", borderColor: "#D9CBEA" }) }} />
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};

/** Create Ticket — Escalation Desk V2. Category/subcategory + SLA + the entire set of fields
 *  to show all come live from GET /api/escalation/config/categories, not a hardcoded form —
 *  confirmed live (pulled from the real V1 tool's own field config) that AWB, description,
 *  attachments and one-off fields (invoice cycle, COD cycle, ...) are each independently
 *  present/required per subcategory, not a fixed set. See EscalationCategoryConfig.FieldDef. */
const CreateEscalationTicket = ({ onClose, onCreated }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await cachedGet(apiConstants.ESCALATION_CATEGORY_CONFIG_V2);
                setCategories(response?.data || []);
            } catch (error) {
                enqueueSnackbar("Could not load the category list", { variant: "error" });
            }
        };
        fetchCategories();
    }, [enqueueSnackbar]);

    // Bootstrap form with just the fixed fields — dynamic per-subcategory fields are registered
    // on the fly (see `selectedFields` below) since we don't know their names until a subcategory
    // is picked.
    // The rules depend on the chosen subcategory, so the resolver reads the latest schema from a
    // ref each time. (Assigning methods.resolver after useForm — what this form did before — is
    // ignored by react-hook-form, so the form never validated anything itself.)
    const validationSchemaRef = useRef(Yup.object());
    const methods = useForm({
        resolver: (values, context, options) => yupResolver(validationSchemaRef.current)(values, context, options),
        defaultValues: {
            merchant: "", category: "", subCategory: "", severity: "P2", visibleToMerchant: false,
            requesterName: "", requesterEmail: "", additionalEmails: "",
        },
    });
    const { handleSubmit, reset, watch, setValue } = methods;
    // AWB -> why it can't take a ticket, from the last check (shown on the AWB chips).
    const [awbProblems, setAwbProblems] = useState({});

    const selectedMerchantId = watch("merchant");
    const selectedCategory = watch("category");
    const selectedSubCategory = watch("subCategory");
    const visibleToMerchant = watch("visibleToMerchant");

    const merchantName = merchants?.find((m) => m.id === selectedMerchantId)?.name || "—";
    // Up to ~2000 merchants: sorted once per list, and picked by typing (see the Merchant field).
    const merchantOptions = useMemo(
        () => _.orderBy(merchants, "name").map((m) => ({ value: m.id, label: m.name })),
        [merchants]
    );

    const categoryOptions = useMemo(
        () => _.orderBy(categories, "categoryName").map((c) => ({ value: c.categoryName, label: c.categoryName })),
        [categories]
    );

    const subCategoryOptions = useMemo(() => {
        const cat = categories.find((c) => c.categoryName === selectedCategory);
        // Switched-off subcategories (Categories tab) aren't offered for new tickets.
        return (cat?.subCategories || []).filter((s) => s.active !== false).map((s) => ({ value: s.name, label: s.name }));
    }, [categories, selectedCategory]);

    const selectedSubCategoryObj = useMemo(() => {
        const cat = categories.find((c) => c.categoryName === selectedCategory);
        return cat?.subCategories?.find((s) => s.name === selectedSubCategory) || null;
    }, [categories, selectedCategory, selectedSubCategory]);

    // The one place all the per-subcategory form behaviour comes from. formKey is unique per
    // slot even when the same field name repeats (some subcategories have two separate file
    // fields, e.g. Weight Disputes' "Upload File" + "Upload Image") — index-suffixed so RHF
    // treats each as an independent input.
    const selectedFields = useMemo(
        () => (selectedSubCategoryObj?.fields || []).map((f, i) => ({
            ...f,
            formKey: `${f.name}__${i}`,
            // Each AWB gets its own ticket (see onSubmit).
            title: f.name === "awbNumber" ? "AWB Number(s) — comma separated, one ticket each" : f.title,
        })),
        [selectedSubCategoryObj]
    );

    // A subcategory belongs to one category: clear it when the category changes, so a stale name
    // can't pass "required" and be sent with the new category (the filter bar does the same).
    const prevCategoryRef = useRef(selectedCategory);
    useEffect(() => {
        if (prevCategoryRef.current === selectedCategory) return;
        prevCategoryRef.current = selectedCategory;
        setValue("subCategory", "");
    }, [selectedCategory, setValue]);

    // Severity starts at the subcategory's mapped default (P2 until a mapping is set).
    useEffect(() => {
        setValue("severity", selectedSubCategoryObj?.defaultSeverity || "P2");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubCategoryObj]);

    const slaLabel = useMemo(() => {
        const fr = selectedSubCategoryObj?.firstResponseMinutes;
        const res = selectedSubCategoryObj?.resolutionMinutes;
        const toH = (m) => (m != null ? Math.round(m / 60) : 4); // matches EscalationConfigService's default fallback (4h/48h)
        return `FR ${toH(fr)}h · Resolve ${res != null ? Math.round(res / 60) : 48}h`;
    }, [selectedSubCategoryObj]);

    // Yup schema rebuilt on every render off selectedFields — same "recompute + let useForm's
    // resolver pick up the latest" pattern this form already used for description-required.
    const dynamicShape = {};
    selectedFields.forEach((f) => {
        if (f.type === "file") {
            dynamicShape[f.formKey] = Yup.mixed().test(
                "required-file",
                `${f.title} is required`,
                (value) => !f.mandatory || !!(value && value.length > 0)
            );
        } else {
            let schema = Yup.string();
            const kind = fieldKind(f);
            if (f.name === "awbNumber") {
                schema = schema.test("awb-count", `Up to ${MAX_AWBS} AWBs at a time — use Bulk Create for more`,
                    (value) => parseAwbs(value).length <= MAX_AWBS);
            } else if (kind === "dateRange") {
                schema = schema.test("date-range", `Pick a start and end date for ${f.title}`,
                    (value) => !value || DATE_RANGE_PATTERN.test(value));
            } else if (kind === "number") {
                schema = schema.test("number", `${f.title} must be a number`,
                    (value) => !value || /^-?\d+(\.\d+)?$/.test(value.trim()));
            } else if (kind === "date") {
                schema = schema.test("date", `Pick a date for ${f.title}`,
                    (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value));
            } else if (kind === "dayCount") {
                schema = schema.test("day-count", "Enter a whole number of days between 1 and 365",
                    (value) => !value || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 365));
            }
            dynamicShape[f.formKey] = f.mandatory ? schema.required(`${f.title} is required`) : schema;
        }
    });

    const validationSchema = Yup.object().shape({
        merchant: Yup.string().required("Please select a merchant"),
        category: Yup.string().required("Please select a category"),
        subCategory: Yup.string().required("Please select a subcategory"),
        severity: Yup.string().oneOf(["P0", "P1", "P2"]),
        // Only asked (and sent) when the ticket is shown on the merchant panel — hidden tickets email nobody.
        requesterEmail: Yup.string().when("visibleToMerchant", {
            is: true,
            then: (schema) => schema.required("An email is required — updates on this ticket go there")
                .test("valid-email", ({ value }) => emailProblem(value), (value) => !value || !emailProblem(value)),
        }),
        additionalEmails: Yup.string().when("visibleToMerchant", {
            is: true,
            then: (schema) => schema.test("valid-emails", ({ value }) => emailListProblem(value), (value) => !emailListProblem(value)),
        }),
        ...dynamicShape,
    });
    validationSchemaRef.current = validationSchema;

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            const payload = {
                merchantId: data.merchant,
                merchantName,
                category: data.category,
                subCategory: data.subCategory,
                severity: data.severity,
                visibleToMerchant: !!data.visibleToMerchant,
                requesterName: data.visibleToMerchant ? data.requesterName || undefined : undefined,
                requesterEmail: data.visibleToMerchant ? data.requesterEmail : undefined,
                additionalEmails: data.visibleToMerchant && data.additionalEmails
                    ? data.additionalEmails.split(",").map((e) => e.trim()).filter(Boolean)
                    : [],
            };

            let awbs = [];
            const customFieldValues = {};
            const files = [];
            selectedFields.forEach((f) => {
                const value = data[f.formKey];
                if (f.name === "awbNumber") {
                    awbs = parseAwbs(value);
                } else if (f.name === "description") {
                    payload.description = value || undefined;
                } else if (f.type === "file") {
                    if (value && value[0]) files.push(value[0]);
                } else if (value) {
                    customFieldValues[f.name] = value;
                }
            });
            if (Object.keys(customFieldValues).length > 0) payload.customFieldValues = customFieldValues;

            // Every AWB gets the same checks a ticket does (the merchant's AWB, not delivered unless
            // allowed, no open ticket for it here) before anything is created: the ones that pass get
            // their tickets, the ones that don't stay in the form, marked with the reason.
            let refused = [];
            if (awbs.length > 0) {
                const check = await postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/check-awbs`,
                    { merchantId: data.merchant, category: data.category, subCategory: data.subCategory, awbNumbers: awbs });
                refused = (check?.data || []).map((p) => ({ awb: p.awb, reason: p.reason }));
                setAwbProblems(Object.fromEntries(refused.map((p) => [p.awb, p.reason])));
                if (refused.length === awbs.length) {
                    enqueueSnackbar(refused.length === 1
                        ? `AWB ${refused[0].awb} ${refused[0].reason}. No ticket was created.`
                        : `None of the ${awbs.length} AWBs can take a ticket (marked in red). No ticket was created.`,
                        { variant: "error", autoHideDuration: 10000 });
                    return;
                }
            }
            const toCreate = awbs.filter((a) => !refused.some((p) => p.awb === a));

            // One ticket per AWB, one after another (each carries the same attachments); an AWB that
            // fails doesn't stop the rest, and stays in the form to fix and retry.
            const created = [];
            const failed = [];
            for (const awb of (toCreate.length > 0 ? toCreate : [undefined])) {
                const formData = new FormData();
                formData.append("data", JSON.stringify({ ...payload, awbNumber: awb }));
                files.forEach((file) => formData.append("attachments", file));
                try {
                    const res = await postGeneric(apiConstants.ESCALATION_CREATE_V2, formData);
                    created.push(res?.data?.escalationNumber);
                } catch (error) {
                    failed.push({ awb, reason: error?.response?.data?.message || "Some error occurred while creating the ticket" });
                }
            }
            if (created.length > 0) {
                enqueueSnackbar(created.length === 1 ? `Ticket ${created[0]} created` : `${created.length} tickets created: ${created.join(", ")}`,
                    { variant: "success" });
                onCreated && onCreated();
            }
            failed.forEach(({ awb, reason }) => enqueueSnackbar(awb ? `AWB ${awb}: ${reason}` : reason, { variant: "error", autoHideDuration: 10000 }));
            refused.forEach(({ awb, reason }) => enqueueSnackbar(`AWB ${awb} ${reason}. No ticket was created for it.`, { variant: "error", autoHideDuration: 10000 }));
            failed.forEach(({ awb, reason }) => { if (awb) refused.push({ awb, reason }); });
            if (refused.length > 0) setAwbProblems(Object.fromEntries(refused.map((p) => [p.awb, p.reason])));
            if (failed.length === 0 && refused.length === 0) {
                reset();
                setAwbProblems({});
                onClose && onClose();
            } else {
                const awbField = selectedFields.find((f) => f.name === "awbNumber");
                // Only the AWBs still without a ticket stay in the field, to fix and retry.
                if (awbField && created.length > 0) setValue(awbField.formKey, refused.map((x) => x.awb).join(", "));
            }
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Some error occurred while creating the ticket", { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>

                {selectedMerchantId && selectedCategory && selectedSubCategory && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>Response targets for this subcategory</Typography>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, border: "1px solid #F3D9AE", bgcolor: "#FDF3E5", borderRadius: "999px", px: 1.25, py: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: "#B45309" }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#B45309" }}>{slaLabel}</Typography>
                        </Box>
                    </Box>
                )}

                <SectionCard icon={<SegmentOutlinedIcon />} title="Classification" sub="Who the ticket is for and what it's about">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RHFSearchableSelectField fullWidth name="merchant" label="Select Merchant" options={merchantOptions} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField label="Category" name="category" options={categoryOptions} width="100%" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField label="Subcategory" name="subCategory" options={subCategoryOptions} width="100%"
                                disabled={!selectedCategory} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField label="Severity" name="severity" options={SEVERITY_OPTIONS} width="100%" />
                        </Grid>
                    </Grid>
                </SectionCard>

                {selectedCategory && selectedSubCategory && selectedFields.length > 0 && (
                    <SectionCard icon={<AssignmentOutlinedIcon />} title="Ticket Details" sub={`Fields for "${selectedSubCategory}"`}>
                        <Grid container spacing={2}>
                            {selectedFields.map((f) => (
                                <Grid item xs={12} md={f.type === "textarea" ? 12 : 6} key={f.formKey}>
                                    {f.type === "textarea" && (
                                        <RHFTextArea name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" rows={4} fullWidth />
                                    )}
                                    {fieldKind(f) === "dateRange" && (
                                        <TicketDateRangeField name={f.formKey} label={f.mandatory ? `${f.title} *` : f.title} />
                                    )}
                                    {fieldKind(f) === "dayCount" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth
                                            type="number" inputProps={{ min: 1, max: 365, step: 1 }} />
                                    )}
                                    {fieldKind(f) === "number" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth
                                            type="number" inputProps={{ step: "any" }} />
                                    )}
                                    {fieldKind(f) === "date" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth
                                            type="date" InputLabelProps={{ shrink: true }} />
                                    )}
                                    {fieldKind(f) === "text" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth />
                                    )}
                                    {f.name === "awbNumber" && <AwbPreview value={watch(f.formKey)} problems={awbProblems} />}
                                    {f.type === "file" && (
                                        <RHFFileField name={f.formKey} label={f.mandatory ? `${f.title} *` : f.title} />
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                )}

                {selectedCategory && selectedSubCategory && (
                    <SectionCard icon={<MailOutlineIcon />} title="Merchant panel & emails" sub="Whether the merchant sees this ticket — and who gets its updates">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <RHFSwitch name="visibleToMerchant" label="Show this ticket on the merchant panel"
                                    helperText={visibleToMerchant
                                        ? "The merchant can see and follow this ticket; the email below gets its updates."
                                        : "Internal only — the merchant won't see it and no emails are sent."} />
                            </Grid>
                            {visibleToMerchant && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <RHFTextField label="Email updates to" name="requesterEmail" fullWidth />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <RHFTextField label="Their name (optional)" name="requesterName" fullWidth />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RHFTextField label="CC emails (comma separated)" name="additionalEmails" fullWidth />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ bgcolor: "#F1ECF7", border: "1px solid #E2D7EE", borderRadius: "10px", px: 1.75, py: 1.25 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#663F95", mb: 0.5 }}>
                                                When are these emails sent?
                                            </Typography>
                                            <Box component="ul" sx={{ m: 0, pl: 2.25, fontSize: 13, color: MUTED, "& li": { mb: 0.25 } }}>
                                                <li>The email above gets a message when the ticket is created, when its status changes, and when a remark marked External is added.</li>
                                                <li>CC emails are copied on those messages for this ticket only — e.g. the merchant's ops contact or account manager for this case.</li>
                                                <li>The &quot;Escalation Desk — Notifications&quot; section on the merchant's Merchant Config page applies to all of that merchant's tickets: it can turn emails off, choose which moments send one (Open = ticket created, Reopened, Pending, Resolved, Closed), and add CC emails that are copied on every ticket.</li>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </SectionCard>
                )}

                {!(selectedCategory && selectedSubCategory) && (
                    <Typography sx={{ fontSize: 13, color: MUTED, px: 0.5 }}>
                        Select a merchant, category, and subcategory to load this ticket's fields.
                    </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                    <Button variant="outlined" sx={resetBtnSx} onClick={() => reset()}>Reset</Button>
                    <LoadingButton loading={isLoading} type="submit" variant="contained" sx={gradientBtnSx} startIcon={<AddIcon />}>
                        Create Ticket
                    </LoadingButton>
                </Box>
            </Box>
        </FormProvider>
    );
};

export default CreateEscalationTicket;
